/**
 * Global trial-expired detection + redirect — Phase 3d of the B2B 14-day
 * Trial go-live.
 *
 * Backend's TrialAccessService.assertActive() throws
 *   ForbiddenException({ error: 'trial_expired' })
 * on every protected surface (10 wiring points per plan §C.3). NestJS's
 * HttpExceptionFilter serialises that to the response body as
 *   { error_code: 'trial_expired', ... }
 * with HTTP 403.
 *
 * Without a global handler, a day-14 revocation surfaces as raw 403 console
 * noise — no conversion route, no explanation to the admin/learner.
 *
 * This helper wraps BOTH `window.fetch` (since many call sites in web-app-2.0
 * use raw fetch) AND the shared axios `api` instance (used by entitlements
 * + a small set of newer features). Any network response with
 *   status in (401, 403) AND body.error_code === 'trial_expired'
 * triggers a one-time redirect to the App Home subscription page.
 *
 * Idempotent: installs itself exactly once; subsequent calls no-op.
 * Safe: only fires on the specific error_code; other 401/403 errors pass
 * through unchanged. Only intercepts same-origin responses with JSON bodies.
 */
import { AxiosInstance, AxiosError } from "axios";

const TRIAL_EXPIRED_ROUTE = `${process.env.REACT_APP_APP_HOME_URL || "https://app.pedagogue.io"}/subscription`;
const TRIAL_EXPIRED_CODE = "trial_expired";
export const TRIAL_KIND_STORAGE_KEY = "pedagogue_trial_kind";

let installed = false;
let redirecting = false;

function navigateToTrialExpired() {
  if (redirecting) {return;}
  redirecting = true;
  window.location.replace(TRIAL_EXPIRED_ROUTE);
}

function bodyIsTrialExpired(body: unknown): boolean {
  if (!body || typeof body !== "object") {return false;}
  const maybe = body as {
    error_code?: unknown;
    details?: { trial_kind?: unknown };
  };
  if (maybe.error_code !== TRIAL_EXPIRED_CODE) {return false;}
  if (maybe.details?.trial_kind === "sample") {
    window.sessionStorage.setItem(TRIAL_KIND_STORAGE_KEY, "sample");
  } else {
    window.sessionStorage.removeItem(TRIAL_KIND_STORAGE_KEY);
  }
  return true;
}

function installFetchInterceptor() {
  const originalFetch = window.fetch.bind(window);
  window.fetch = async (input, init) => {
    const response = await originalFetch(input, init);
    if (response.status === 401 || response.status === 403) {
      const contentType = response.headers.get("content-type") ?? "";
      if (contentType.includes("application/json")) {
        try {
          const clone = response.clone();
          const body = await clone.json();
          if (bodyIsTrialExpired(body)) {
            navigateToTrialExpired();
          }
        } catch {
          // body not JSON-parseable; ignore
        }
      }
    }
    return response;
  };
}

export function installAxiosTrialExpiredInterceptor(instance: AxiosInstance) {
  instance.interceptors.response.use(
    (response) => response,
    (error: AxiosError) => {
      const status = error.response?.status;
      if (status === 401 || status === 403) {
        if (bodyIsTrialExpired(error.response?.data)) {
          navigateToTrialExpired();
        }
      }
      return Promise.reject(error);
    },
  );
}

export function installTrialExpiredHandler(axiosInstances: AxiosInstance[] = []) {
  if (installed) {return;}
  installed = true;
  installFetchInterceptor();
  for (const instance of axiosInstances) {
    installAxiosTrialExpiredInterceptor(instance);
  }
}
